/**
 * Sequential execution of a prepared manufacture import.
 *
 * One manufacture at a time, through the injected app writer (`(info) => saveNewManufacture(db, info)`
 * in the screen): every manufacture increments the same `dailyStats` document for its business day
 * and the same product stock fields, and concurrent batches on one document contend. Sequential is
 * also what keeps a failure local to one manufacture and the progress counter meaningful.
 *
 * A failure — the writer's INGREDIENT_NOT_FOUND refusal, a network error, a write-mode refusal
 * from `assertWWriterAvailable` — never stops the run: the remaining manufactures are independent,
 * and the report tells the user exactly which file rows to retry.
 */

import { type RowRef } from './types'
import { importFailureMessage } from '../../global/Firebase/writeOutcome'

export interface ManufactureImportItem<Info> {
  key: string
  info: Info
  refs: RowRef[]
}

export interface ManufactureImportFailure {
  key: string
  refs: RowRef[]
  /** Technical message (an error code where one exists); the screen translates it. */
  message: string
}

export interface ManufactureImportRunResult {
  created: number
  failures: ManufactureImportFailure[]
}

export interface RunManufactureImportOptions<Info> {
  items: Array<ManufactureImportItem<Info>>
  saveManufacture: (info: Info) => Promise<unknown>
  onProgress?: (completed: number, total: number) => void
}

/** Same formatting as utils/salesImport/runImport's failureMessage. */
const failureMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message: unknown }).message)
    // saveNewManufacture throws this, before writing anything, for an ingredient not in the catalog.
    if (message === 'INGREDIENT_NOT_FOUND') {
      const details = error as { ingredientName?: string; productName?: string }
      return `INGREDIENT_NOT_FOUND: ${details.productName ?? ''} / ${details.ingredientName ?? ''}`
    }
    return message
  }
  return String(error)
}

export const runManufactureImport = async <Info>({
  items,
  saveManufacture,
  onProgress,
}: RunManufactureImportOptions<Info>): Promise<ManufactureImportRunResult> => {
  const result: ManufactureImportRunResult = { created: 0, failures: [] }

  for (let index = 0; index < items.length; index++) {
    const { key, info, refs } = items[index]
    try {
      await saveManufacture(info)
      result.created += 1
    } catch (error) {
      result.failures.push({ key, refs, message: importFailureMessage(error, failureMessage(error)) })
    }
    onProgress?.(index + 1, items.length)
  }

  return result
}
